import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
    Dices, Trophy, Calendar, Users, BarChart3, 
    ChevronRight, Check, Star, Sparkles, ArrowRight,
    Mail, Loader2, KeyRound, Globe
} from 'lucide-react';
import { useI18n, SUPPORTED_LANGUAGES } from '../i18n';
import { formatMessage } from '../locales/app';

/** Build date from CRA env (set at `npm run build`); fallback = today (dev) */
function getReleaseDateLabel(language) {
    const iso = process.env.REACT_APP_BUILD_DATE || new Date().toISOString().slice(0, 10);
    const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10));
    if (!y || !m || !d) return iso;
    const locale = language === 'sv' ? 'sv-SE' : language === 'en' ? 'en-GB' : 'fi-FI';
    const parsed = new Date(y, m - 1, d);
    if (!Number.isNaN(parsed.getTime())) {
        return parsed.toLocaleDateString(locale);
    }
    return `${d}.${m}.${y}`;
}

const FEATURE_ICONS = [Trophy, Calendar, BarChart3, Users, Dices, Sparkles];

const LANGUAGE_LABELS = { fi: 'Suomi', en: 'English', sv: 'Svenska' };

/**
 * LandingPage - Public-facing landing page for the SaaS platform
 */
const LandingPage = ({ onStartOnboarding }) => {
    const navigate = useNavigate();
    const { t, language, setLanguage } = useI18n();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    /** idle | existing | code | magic_sent */
    const [step, setStep] = useState('idle');
    const [purpose, setPurpose] = useState('onboarding');
    const [existingClubName, setExistingClubName] = useState('');
    const [code, setCode] = useState('');
    const [devCode, setDevCode] = useState('');
    const [error, setError] = useState('');
    const [magicLinkSent, setMagicLinkSent] = useState(true);
    const releaseDateLabel = getReleaseDateLabel(language);
    const copyrightYear = new Date().getFullYear();

    const requestCode = async ({ confirmExisting = false } = {}) => {
        const res = await fetch('/api/onboarding/start', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, confirmExisting })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new Error(data.error || t.landing.genericError);
        }
        return data;
    };

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        if (!email || !email.includes('@')) {
            setError(t.landing.invalidEmail);
            return;
        }

        setLoading(true);
        setError('');

        try {
            const data = await requestCode({ confirmExisting: false });
            if (data.existingAccount || data.status === 'existing') {
                setExistingClubName(data.clubName || '');
                setStep('existing');
                return;
            }
            setPurpose(data.purpose || 'onboarding');
            setDevCode(data.devCode || '');
            setCode('');
            setStep('code');
        } catch (err) {
            setError(err.message || t.landing.connectionError);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmReverify = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await requestCode({ confirmExisting: true });
            setPurpose(data.purpose || 'relogin');
            setDevCode(data.devCode || '');
            setCode('');
            setStep('code');
        } catch (err) {
            setError(err.message || t.landing.codeSendFailed);
        } finally {
            setLoading(false);
        }
    };

    const handleVerify = async (e) => {
        e.preventDefault();
        if (!code || code.trim().length !== 6) {
            setError(t.landing.invalidCode);
            return;
        }

        setLoading(true);
        setError('');

        try {
            const res = await fetch('/api/onboarding/verify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, code: code.trim() })
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data.error || t.landing.verificationFailed);
                return;
            }

            if (data.purpose === 'relogin') {
                setMagicLinkSent(data.magicLinkSent !== false);
                setStep('magic_sent');
                return;
            }

            const payload = { email, verificationCode: code.trim() };
            if (onStartOnboarding) {
                onStartOnboarding(payload);
            }
            navigate('/onboarding', { state: payload });
        } catch (err) {
            setError(t.landing.connectionError);
        } finally {
            setLoading(false);
        }
    };

    const resetToEmail = () => {
        setStep('idle');
        setPurpose('onboarding');
        setExistingClubName('');
        setCode('');
        setDevCode('');
        setError('');
    };

    return (
        <div className="landing-page">
            <div className="landing-language-bar">
                <Globe size={16} aria-hidden="true" />
                <span className="landing-language-label">{t.landing.languageLabel}</span>
                <div className="landing-language-options" role="group" aria-label={t.landing.languageLabel}>
                    {SUPPORTED_LANGUAGES.map((lang) => (
                        <button
                            key={lang}
                            type="button"
                            className={`landing-language-btn ${language === lang ? 'active' : ''}`}
                            onClick={() => setLanguage(lang)}
                            aria-pressed={language === lang}
                        >
                            {LANGUAGE_LABELS[lang]}
                        </button>
                    ))}
                </div>
            </div>

            {/* Hero Section */}
            <section className="hero">
                <div className="hero-content">
                    <div className="hero-badge">
                        <Sparkles size={14} /> {t.landing.latestVersion} {releaseDateLabel}
                    </div>
                    <h1>
                        {t.landing.heading}
                        <span className="gradient-text">{t.landing.headingAccent}</span>
                    </h1>
                    <p className="hero-subtitle">
                        {t.landing.subtitle}
                    </p>
                    
                    {/* Email Signup / re-login */}
                    {step === 'idle' && (
                        <form className="signup-form" onSubmit={handleEmailSubmit}>
                            <div className="input-wrapper">
                                <Mail size={18} />
                                <input 
                                    type="email" 
                                    placeholder={t.landing.emailPlaceholder}
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                />
                            </div>
                            <button type="submit" disabled={loading}>
                                {loading ? <Loader2 size={18} className="spin" /> : t.landing.startFree}
                                {!loading && <ChevronRight size={18} />}
                            </button>
                        </form>
                    )}

                    {step === 'existing' && (
                        <div className="signup-form existing-account-panel">
                            <div className="success-message">
                                <Mail size={20} />
                                <span>
                                    {formatMessage(t.landing.existingAccountRegistered, { email })}
                                    {existingClubName
                                        ? formatMessage(t.landing.existingAccountWithClub, { clubName: existingClubName })
                                        : ''}.
                                </span>
                            </div>
                            <p className="existing-account-question">
                                {t.landing.existingAccountQuestion}
                            </p>
                            <button type="button" disabled={loading} onClick={handleConfirmReverify}>
                                {loading ? <Loader2 size={18} className="spin" /> : t.landing.confirmAccount}
                                {!loading && <ChevronRight size={18} />}
                            </button>
                            <button
                                type="button"
                                className="link-button"
                                disabled={loading}
                                onClick={resetToEmail}
                            >
                                {t.landing.useDifferentEmail}
                            </button>
                        </div>
                    )}

                    {step === 'code' && (
                        <form className="signup-form verify-form" onSubmit={handleVerify}>
                            <div className="success-message">
                                <Check size={20} />
                                <span>
                                    {purpose === 'relogin'
                                        ? t.landing.checkEmailRelogin
                                        : t.landing.checkEmailCode}
                                </span>
                            </div>
                            <div className="input-wrapper">
                                <KeyRound size={18} />
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={6}
                                    placeholder={t.landing.codePlaceholder}
                                    value={code}
                                    onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    autoFocus
                                />
                            </div>
                            <button type="submit" disabled={loading}>
                                {loading ? <Loader2 size={18} className="spin" /> : t.landing.verify}
                                {!loading && <ChevronRight size={18} />}
                            </button>
                            <button
                                type="button"
                                className="link-button"
                                disabled={loading}
                                onClick={async () => {
                                    setLoading(true);
                                    setError('');
                                    try {
                                        const data = await requestCode({
                                            confirmExisting: purpose === 'relogin'
                                        });
                                        if (data.existingAccount) {
                                            setExistingClubName(data.clubName || '');
                                            setStep('existing');
                                            return;
                                        }
                                        setDevCode(data.devCode || '');
                                    } catch (err) {
                                        setError(err.message || t.landing.codeResendFailed);
                                    } finally {
                                        setLoading(false);
                                    }
                                }}
                            >
                                {t.landing.resendCode}
                            </button>
                            <button
                                type="button"
                                className="link-button"
                                disabled={loading}
                                onClick={resetToEmail}
                            >
                                {t.landing.cancel}
                            </button>
                            {devCode && (
                                <p className="dev-code-hint">
                                    {formatMessage(t.landing.devCodeHint, { code: devCode })}
                                </p>
                            )}
                        </form>
                    )}

                    {step === 'magic_sent' && (
                        <div className="signup-form existing-account-panel">
                            <div className="success-message">
                                <Check size={20} />
                                <span>
                                    {magicLinkSent
                                        ? t.landing.magicLinkSent
                                        : t.landing.magicLinkFailed}
                                </span>
                            </div>
                            <button type="button" className="link-button" onClick={resetToEmail}>
                                {t.landing.backToStart}
                            </button>
                        </div>
                    )}
                    {error && <p className="error-text">{error}</p>}
                    
                    <div className="hero-trust">
                        <span>✓ {t.landing.freeUse}</span>
                        <span>✓ {t.landing.noCard}</span>
                        <span>✓ {t.landing.readyFast}</span>
                    </div>
                </div>
                
                <div className="hero-visual">
                    <div className="app-preview">
                        <div className="preview-header">
                            <div className="preview-dots">
                                <span></span><span></span><span></span>
                            </div>
                            <span>{t.landing.clubPreview}</span>
                        </div>
                        <div className="preview-content">
                            <div className="preview-leaderboard">
                                <div className="preview-row gold">
                                    <Trophy size={16} /> Masa - 142 MP
                                </div>
                                <div className="preview-row silver">
                                    <span>2.</span> Jari - 128 MP
                                </div>
                                <div className="preview-row bronze">
                                    <span>3.</span> Henkka - 115 MP
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="features" id="features">
                <h2>{t.landing.featuresHeading}</h2>
                <div className="features-grid">
                    {(t.landing.features || []).map(([title, description], index) => (
                        <FeatureCard
                            key={title}
                            icon={FEATURE_ICONS[index]}
                            title={title}
                            description={description}
                        />
                    ))}
                </div>
            </section>

            {/* Social Proof */}
            <section className="social-proof">
                <div className="testimonial">
                    <div className="stars">
                        {[1,2,3,4,5].map(i => <Star key={i} size={20} fill="#f59e0b" stroke="#f59e0b" />)}
                    </div>
                    <blockquote>{t.landing.quote}</blockquote>
                    <cite>{t.landing.quoteAuthor}</cite>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <h2>{t.landing.ctaHeading}</h2>
                <p>{t.landing.ctaText}</p>
                <button className="cta-button" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                    {t.landing.createClub} <ArrowRight size={18} />
                </button>
            </section>

            {/* Footer */}
            <footer className="landing-footer">
                <div className="footer-content">
                    <div className="footer-brand">
                        <Dices size={24} /> {t.landing.footerBrand}
                    </div>
                    <div className="footer-navigation">
                        <a href="#features">{t.landing.featuresLink}</a>
                        <details className="footer-legal-menu">
                            <summary>{t.legal.menu}</summary>
                            <nav aria-label={t.legal.menu}>
                                <Link to="/legal/license">{t.legal.licenseLink}</Link>
                                <Link to="/legal/privacy">{t.legal.privacyLink}</Link>
                                <Link to="/legal/terms">{t.legal.termsLink}</Link>
                            </nav>
                        </details>
                    </div>
                    <p>© {copyrightYear} {t.landing.footerCompany}. {t.landing.copyright}</p>
                </div>
            </footer>

            <style>{`
                .landing-page {
                    min-height: 100vh;
                    width: 100%;
                    max-width: 100vw;
                    overflow-x: hidden;
                    background: linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%);
                    color: #f0f6fc;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                }

                .landing-language-bar {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 0.75rem;
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 1rem 2rem 0;
                    color: #9ca3af;
                    font-size: 0.85rem;
                }

                .landing-language-label {
                    margin-right: 0.25rem;
                }

                .landing-language-options {
                    display: flex;
                    gap: 0.35rem;
                }

                .landing-language-btn {
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid #374151;
                    border-radius: 8px;
                    color: #9ca3af;
                    cursor: pointer;
                    font-size: 0.8rem;
                    padding: 0.35rem 0.65rem;
                    transition: border-color 0.2s, color 0.2s, background 0.2s;
                }

                .landing-language-btn:hover {
                    border-color: #8b5cf6;
                    color: #f0f6fc;
                }

                .landing-language-btn.active {
                    background: rgba(139, 92, 246, 0.2);
                    border-color: #8b5cf6;
                    color: #a78bfa;
                }

                /* Hero */
                .hero {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 4rem;
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 4rem 2rem;
                    min-height: 80vh;
                    align-items: center;
                }

                .hero-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    background: rgba(139, 92, 246, 0.15);
                    border: 1px solid rgba(139, 92, 246, 0.3);
                    padding: 0.5rem 1rem;
                    border-radius: 20px;
                    font-size: 0.85rem;
                    color: #a78bfa;
                    margin-bottom: 1.5rem;
                }

                .hero h1 {
                    font-size: 3.5rem;
                    font-weight: 800;
                    line-height: 1.1;
                    margin: 0 0 1.5rem 0;
                }

                .gradient-text {
                    background: linear-gradient(135deg, #8b5cf6, #ec4899, #f59e0b);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }

                .hero-subtitle {
                    font-size: 1.25rem;
                    color: #9ca3af;
                    line-height: 1.6;
                    margin-bottom: 2rem;
                }

                .signup-form {
                    display: flex;
                    gap: 0.75rem;
                    margin-bottom: 1.5rem;
                }

                .input-wrapper {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    background: rgba(255, 255, 255, 0.05);
                    border: 2px solid #374151;
                    border-radius: 12px;
                    padding: 0 1rem;
                    flex: 1;
                    transition: border-color 0.2s;
                }

                .input-wrapper:focus-within {
                    border-color: #8b5cf6;
                }

                .input-wrapper svg {
                    color: #6b7280;
                }

                .input-wrapper input {
                    flex: 1;
                    background: transparent;
                    border: none;
                    padding: 1rem 0;
                    color: #f0f6fc;
                    font-size: 1rem;
                    outline: none;
                }

                .signup-form button[type="submit"] {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0 2rem;
                    background: linear-gradient(135deg, #8b5cf6, #7c3aed);
                    border: none;
                    border-radius: 12px;
                    color: white;
                    font-weight: 600;
                    font-size: 1rem;
                    cursor: pointer;
                    transition: transform 0.2s, box-shadow 0.2s;
                }

                .signup-form button[type="submit"]:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 30px rgba(139, 92, 246, 0.3);
                }

                .signup-form button[type="submit"]:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                .success-message {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    background: rgba(34, 197, 94, 0.15);
                    border: 1px solid rgba(34, 197, 94, 0.3);
                    padding: 1rem 1.5rem;
                    border-radius: 12px;
                    color: #22c55e;
                    margin-bottom: 1.5rem;
                    width: 100%;
                }

                .verify-form {
                    flex-wrap: wrap;
                    align-items: stretch;
                }

                .verify-form .success-message {
                    flex: 1 1 100%;
                }

                .verify-form .input-wrapper input {
                    letter-spacing: 0.35em;
                    font-weight: 600;
                }

                .link-button {
                    background: none;
                    border: none;
                    color: #a78bfa;
                    cursor: pointer;
                    font-size: 0.9rem;
                    padding: 0.25rem 0;
                    width: 100%;
                    text-align: left;
                }

                .link-button:hover {
                    text-decoration: underline;
                }

                .existing-account-panel {
                    flex-direction: column;
                    align-items: stretch;
                    gap: 0.75rem;
                }

                .existing-account-question {
                    margin: 0;
                    color: rgba(255, 255, 255, 0.85);
                    font-size: 0.95rem;
                    line-height: 1.45;
                    width: 100%;
                }

                .existing-account-panel .success-message {
                    margin-bottom: 0;
                    width: 100%;
                }

                .dev-code-hint {
                    width: 100%;
                    margin: 0;
                    font-size: 0.8rem;
                    color: #9ca3af;
                }

                .error-text {
                    color: #ef4444;
                    font-size: 0.9rem;
                    margin-top: -1rem;
                    margin-bottom: 1rem;
                }

                .hero-trust {
                    display: flex;
                    gap: 2rem;
                    font-size: 0.9rem;
                    color: #6b7280;
                }

                /* App Preview */
                .hero-visual {
                    display: flex;
                    justify-content: center;
                }

                .app-preview {
                    background: #161b22;
                    border: 1px solid #30363d;
                    border-radius: 16px;
                    overflow: hidden;
                    width: 100%;
                    max-width: 400px;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
                }

                .preview-header {
                    background: #21262d;
                    padding: 0.75rem 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    border-bottom: 1px solid #30363d;
                }

                .preview-dots {
                    display: flex;
                    gap: 6px;
                }

                .preview-dots span {
                    width: 12px;
                    height: 12px;
                    border-radius: 50%;
                    background: #30363d;
                }

                .preview-dots span:first-child { background: #ef4444; }
                .preview-dots span:nth-child(2) { background: #f59e0b; }
                .preview-dots span:last-child { background: #22c55e; }

                .preview-content {
                    padding: 1.5rem;
                }

                .preview-leaderboard {
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                }

                .preview-row {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 0.75rem 1rem;
                    background: rgba(255, 255, 255, 0.03);
                    border-radius: 8px;
                    font-weight: 500;
                }

                .preview-row.gold {
                    background: rgba(245, 158, 11, 0.15);
                    color: #f59e0b;
                }

                .preview-row.silver { color: #9ca3af; }
                .preview-row.bronze { color: #cd7f32; }

                /* Features */
                .features {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 4rem 2rem;
                }

                .features h2 {
                    text-align: center;
                    font-size: 2rem;
                    margin-bottom: 3rem;
                }

                .features-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 1.5rem;
                }

                .feature-card {
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid #30363d;
                    border-radius: 16px;
                    padding: 2rem;
                    transition: transform 0.2s, border-color 0.2s;
                }

                .feature-card:hover {
                    transform: translateY(-4px);
                    border-color: #8b5cf6;
                }

                .feature-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    background: rgba(139, 92, 246, 0.15);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #a78bfa;
                    margin-bottom: 1rem;
                }

                .feature-card h3 {
                    margin: 0 0 0.5rem 0;
                    font-size: 1.1rem;
                }

                .feature-card p {
                    margin: 0;
                    color: #8b949e;
                    line-height: 1.6;
                }

                /* Social Proof */
                .social-proof {
                    max-width: 800px;
                    margin: 0 auto;
                    padding: 4rem 2rem;
                    text-align: center;
                }

                .testimonial {
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid #30363d;
                    border-radius: 16px;
                    padding: 3rem;
                }

                .stars {
                    display: flex;
                    justify-content: center;
                    gap: 0.25rem;
                    margin-bottom: 1.5rem;
                }

                .testimonial blockquote {
                    font-size: 1.25rem;
                    font-style: italic;
                    margin: 0 0 1rem 0;
                    line-height: 1.6;
                }

                .testimonial cite {
                    color: #8b949e;
                    font-style: normal;
                }

                /* CTA */
                .cta-section {
                    text-align: center;
                    padding: 4rem 2rem;
                    background: linear-gradient(180deg, transparent, rgba(139, 92, 246, 0.1));
                }

                .cta-section h2 {
                    font-size: 2rem;
                    margin-bottom: 0.5rem;
                }

                .cta-section p {
                    color: #8b949e;
                    margin-bottom: 2rem;
                }

                .cta-button {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 1rem 2rem;
                    background: linear-gradient(135deg, #8b5cf6, #7c3aed);
                    border: none;
                    border-radius: 12px;
                    color: white;
                    font-weight: 600;
                    font-size: 1.1rem;
                    cursor: pointer;
                    transition: transform 0.2s, box-shadow 0.2s;
                }

                .cta-button:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 30px rgba(139, 92, 246, 0.3);
                }

                /* Footer */
                .landing-footer {
                    border-top: 1px solid #30363d;
                    padding: 2rem;
                }

                .footer-content {
                    max-width: 1200px;
                    margin: 0 auto;
                    text-align: center;
                }

                .footer-brand {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    font-weight: 600;
                    margin-bottom: 1rem;
                }

                .footer-navigation {
                    display: flex;
                    align-items: flex-start;
                    justify-content: center;
                    gap: 2rem;
                    margin-bottom: 1rem;
                }

                .footer-navigation a,
                .footer-legal-menu summary {
                    color: #8b949e;
                    text-decoration: none;
                    transition: color 0.2s;
                }

                .footer-navigation > a {
                    line-height: 2.25rem;
                }

                .footer-legal-menu {
                    position: relative;
                }

                .footer-legal-menu summary {
                    list-style: none;
                    cursor: pointer;
                    line-height: 2.25rem;
                    padding: 0 0.75rem;
                    border: 1px solid #30363d;
                    border-radius: 8px;
                    user-select: none;
                }

                .footer-legal-menu summary::-webkit-details-marker {
                    display: none;
                }

                .footer-legal-menu summary::after {
                    content: ' ▾';
                    color: #a78bfa;
                }

                .footer-legal-menu[open] summary::after {
                    content: ' ▴';
                }

                .footer-legal-menu nav {
                    position: absolute;
                    z-index: 2;
                    left: 50%;
                    bottom: calc(100% + 0.5rem);
                    transform: translateX(-50%);
                    width: max-content;
                    max-width: min(18rem, calc(100vw - 2rem));
                    display: flex;
                    flex-direction: column;
                    gap: 0.25rem;
                    padding: 0.5rem;
                    border: 1px solid #30363d;
                    border-radius: 10px;
                    background: #161b2d;
                    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.35);
                }

                .footer-legal-menu nav a {
                    padding: 0.65rem 0.85rem;
                    border-radius: 6px;
                    white-space: normal;
                }

                .footer-navigation a:hover,
                .footer-legal-menu summary:hover {
                    color: #f0f6fc;
                }

                .footer-content p {
                    color: #6b7280;
                    font-size: 0.85rem;
                }

                .spin { animation: spin 1s linear infinite; }
                @keyframes spin { to { transform: rotate(360deg); } }

                /* Responsive */
                @media (max-width: 968px) {
                    .landing-language-bar {
                        justify-content: center;
                        padding: 1rem 1rem 0;
                    }

                    .hero {
                        grid-template-columns: 1fr;
                        text-align: center;
                        padding: 2rem 1rem;
                    }

                    .hero h1 {
                        font-size: 2.5rem;
                    }

                    .signup-form {
                        flex-direction: column;
                    }

                    .signup-form button[type="submit"] {
                        justify-content: center;
                        padding: 1rem;
                    }

                    .hero-trust {
                        justify-content: center;
                        flex-wrap: wrap;
                        gap: 1rem;
                    }

                    .hero-visual {
                        order: -1;
                    }

                    .app-preview {
                        max-width: 320px;
                    }
                }

                @media (max-width: 600px) {
                    .landing-language-bar {
                        gap: 0.4rem;
                    }

                    .landing-language-label {
                        display: none;
                    }

                    .landing-language-btn {
                        padding: 0.4rem 0.55rem;
                    }

                    .hero {
                        min-height: auto;
                        gap: 2rem;
                        padding-top: 1.5rem;
                    }

                    .hero h1 {
                        font-size: clamp(2rem, 11vw, 2.5rem);
                    }

                    .features,
                    .social-proof,
                    .cta-section {
                        padding: 3rem 1rem;
                    }

                    .features-grid {
                        grid-template-columns: minmax(0, 1fr);
                    }

                    .feature-card {
                        padding: 1.5rem;
                    }

                    .testimonial {
                        padding: 2rem 1.25rem;
                    }

                    .landing-footer {
                        padding: 1.5rem 1rem;
                    }

                    .footer-navigation {
                        align-items: center;
                        flex-direction: column;
                        gap: 0.4rem;
                    }

                    .footer-navigation > a {
                        line-height: 1.5rem;
                    }

                    .footer-legal-menu summary {
                        min-width: 12rem;
                    }

                    .footer-content p {
                        line-height: 1.5;
                    }
                }
            `}</style>
        </div>
    );
};

// Feature Card Component
const FeatureCard = ({ icon: Icon, title, description }) => (
    <div className="feature-card">
        <div className="feature-icon">
            <Icon size={24} />
        </div>
        <h3>{title}</h3>
        <p>{description}</p>
    </div>
);

export default LandingPage;
